import dayjs from "dayjs";
import {
  IJobDetails,
  IJobDetailsCompletedStatuses,
} from "../../api/jobsService/IJobsService";
import { DownloadNotification } from "../../components/Layout/components/Header/components/Notifications/hooks/useNotifications/useNotifications";
import {
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "../../utils/constants";
import {
  DOWNLOAD_ALL_ORDERS_JOB_COMPLETE,
  DOWNLOAD_NEW_JOB_COMPLETE,
  DOWNLOAD_ORDERS_BY_LINE_ITEM_JOB_COMPLETE,
  NOTIFY,
} from "../../utils/eventConstants";
import { formatTimestamp } from "../../utils/formatters";
import {
  PendingCompilationJob,
  removeCompletedCompilationJobs,
  removeExpiredCompilationJobs,
} from "../../utils/notificationsUtils";

const raiseCompileJobCompleteEvent = (
  downloadedJobId: string,
  type: string
): void => {
  if (type === "All Orders Download")
    window.dispatchEvent(
      new CustomEvent(DOWNLOAD_ALL_ORDERS_JOB_COMPLETE, {
        bubbles: true,
        detail: {
          downloadedJobId,
        },
      })
    );
  else if (type === "Orders by Line Item Download")
    window.dispatchEvent(
      new CustomEvent(DOWNLOAD_ORDERS_BY_LINE_ITEM_JOB_COMPLETE, {
        bubbles: true,
        detail: {
          downloadedJobId,
        },
      })
    );
  else
    window.dispatchEvent(
      new CustomEvent(DOWNLOAD_NEW_JOB_COMPLETE, {
        bubbles: true,
        detail: {
          downloadedJobId,
        },
      })
    );
};

const raiseCompileNotificationEvent = (payload: DownloadNotification): void => {
  window.dispatchEvent(
    new CustomEvent(NOTIFY, {
      bubbles: true,
      detail: payload,
    })
  );
};

export const filterCompletedJobStatuses = (
  downloadStatuses: IJobDetails[]
): IJobDetailsCompletedStatuses[] =>
  downloadStatuses.filter(
    (job) => job.status !== "IN_PROGRESS"
  ) as IJobDetailsCompletedStatuses[];

export const publishCompletedCompilationJobStatuses = (
  completedJobStatuses: IJobDetailsCompletedStatuses[],
  pendingJobs?: PendingCompilationJob[]
) => {
  if (completedJobStatuses.length > 0) {
    completedJobStatuses.forEach((job) => {
      const metadata = JSON.parse(job.metadata);
      raiseCompileJobCompleteEvent(job.id, metadata?.type);
      const timestamp = formatTimestamp(
        new Date(job.createdDate).toUTCString()
      );

      const accountIdentifier =
        job.accountId ??
        (job.accountIds?.length > 1 ? "CONSOLIDATED" : job.accountIds?.[0]);

      const matchingPendingJob = pendingJobs?.find(
        (pendingJob) => pendingJob.jobId === job.id
      );
      let filename = matchingPendingJob?.fileName;
      let displayName = matchingPendingJob?.displayName;

      if (!filename) {
        filename =
          metadata.type === "All Orders Download"
            ? getFileNameForAllOrders()
            : `${getFilenameLabel(
                accountIdentifier,
                metadata.type
              )}_${timestamp}`;

        if (metadata.type === "Orders by Line Item Download") {
          filename = getFileNameForOrdersByLineItem();
        }
      }

      if (!displayName) {
        displayName = filename;
      }
      let compilationStatus = job.status;
      if (
        Array.isArray(metadata.error) &&
        metadata.error.length > 0 &&
        job.status === "COMPLETED"
      ) {
        compilationStatus = "PARTIAL_SUCCESS";
      }

      raiseCompileNotificationEvent({
        id: job.id,
        filename,
        displayName,
        downloadUrl: job.downloadUrl,
        compilationStatus,
        type: metadata.type,
        createdDate: job.createdDate,
        metadata,
      } as DownloadNotification);
    });
  }
};

const getFilenameLabel = (accountId: string, type: string) => {
  if (type === INVOICE_CONSOLIDATION) {
    return `${accountId}_CONSOLIDATED_INVOICES`;
  }

  if (
    type === INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION ||
    type === INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION
  ) {
    return "Consolidated_Invoices";
  }

  if (type === RETURNS_EXCEL) {
    return `${accountId}_RETURNS`;
  }

  if (type === RETURN_SUMMARY_EXCEL) {
    return `${accountId}_RETURN_SUMMARY`;
  }

  return `${accountId}_INVOICES`;
};

const getFileNameForAllOrders = () => {
  const yyMMddHHmmss = dayjs().format("DDMMYYHHmmss");
  return `All_Orders_${yyMMddHHmmss}`;
};

const getFileNameForOrdersByLineItem = () => {
  const yyMMddHHmmss = dayjs().format("DDMMYYHHmmss");
  return `All_Lines_${yyMMddHHmmss}`;
};

export const publishExpiredCompilationJobStatuses = (
  expiredJobs: PendingCompilationJob[]
) => {
  if (expiredJobs.length > 0) {
    expiredJobs.forEach((job) => {
      raiseCompileJobCompleteEvent(job.jobId, job.type);
      raiseCompileNotificationEvent({
        id: job.jobId,
        downloadUrl: null,
        compilationStatus: "FAILED",
        type: job.type,
      } as DownloadNotification);
    });
  }
};

export const removeCompletedAndExpiredCompilationJobs = (
  accountId: string,
  completedJobStatuses: IJobDetailsCompletedStatuses[],
  PROMOTION_DOWNLOAD_JOB_TIMEOUT: number
): {
  remainingJobs: PendingCompilationJob[];
  expiredJobs: PendingCompilationJob[];
} => {
  const remainingJobs = removeCompletedCompilationJobs(
    accountId,
    completedJobStatuses.map((job) => job.id)
  );
  const { expiredJobs, validJobs } = removeExpiredCompilationJobs(
    accountId,
    remainingJobs,
    PROMOTION_DOWNLOAD_JOB_TIMEOUT
  );
  return { remainingJobs: validJobs, expiredJobs };
};
